import interact from 'interactjs';
import { DEFAULT_RELEASE_PROFILE, getReleaseDurationMs, shouldCloseByDistanceOrFlick } from '../../core/drag';
import { DEFAULT_DRAG_BASE, readCssNumber, readNumberSetting, readOptionalNumberSetting } from '../../core/motion';
import './styles.css';

const DRAG_CLOSE_DISTANCE_PX = 96;
const MODAL_DRAG_SELECTOR = '.modal .modal-box[data-draggable="true"]';
const MODAL_BOUND_KEY = 'modal';
const MODAL_OVERLAY_CLOSE_KEY = 'modalOverlayClose';
const DIALOG_MODAL_BOUND_KEY = 'dialogModalBound';
let modalBound = false;

/**
 * dialog.modal 固有の処理をバインドする。
 * modal-no-transition-once: open 直前（beforetoggle）に自動除去
 *   → HTML に open 属性で初期表示した場合はトランジションなし
 *   → showModal() 以降はトランジションあり
 */
function bindDialogModalBehavior(dialog: HTMLDialogElement): void {
    if (dialog.dataset[DIALOG_MODAL_BOUND_KEY]) return;
    dialog.dataset[DIALOG_MODAL_BOUND_KEY] = '1';

    dialog.addEventListener('beforetoggle', () => {
        dialog.classList.remove('modal-no-transition-once');
    });
}

/** dialog.modal は dialog.close()、div.modal はcheckboxをunchecked にして閉じる */
function closeModal(modalEl: HTMLElement): void {
    if (modalEl instanceof HTMLDialogElement) {
        modalEl.close();
        return;
    }
    const prev = modalEl.previousElementSibling;
    if (prev instanceof HTMLInputElement && prev.classList.contains('modal-toggle')) {
        prev.checked = false;
        prev.dispatchEvent(new Event('change'));
    }
}

/**
 * div.modal のオーバーレイクローズを設定する。
 * (dialog.modal は <form method="dialog" class="modal-backdrop"> が担う)
 */
function bindDivModalBehavior(divModal: HTMLElement): void {
    if (divModal.dataset[MODAL_OVERLAY_CLOSE_KEY]) return;
    divModal.dataset[MODAL_OVERLAY_CLOSE_KEY] = '1';

    divModal.addEventListener('click', (e) => {
        if (e.target === divModal) closeModal(divModal);
    });
}

export function initModalCustom(): void {
    if (modalBound) return;
    modalBound = true;
    document.addEventListener('ui:update', () => {
        initModalDraggables(document);
    });
}

export function initModalDraggables(scope: ParentNode): void {
    const targets = Array.from(scope.querySelectorAll<HTMLElement>(MODAL_DRAG_SELECTOR));
    targets.forEach((el) => {
        if (el.dataset.draggableBound === MODAL_BOUND_KEY) return;
        el.dataset.draggableBound = MODAL_BOUND_KEY;
        const handleSelector = el.dataset.dragHandle || '';
        const interactable = bindDialogLikeDrag(el, handleSelector);
        el.addEventListener('remove', () => interactable.unset(), { once: true });

        const divModal = el.closest<HTMLElement>('div.modal');
        if (divModal) bindDivModalBehavior(divModal);

        const dialogModal = el.closest<HTMLDialogElement>('dialog.modal');
        if (dialogModal) bindDialogModalBehavior(dialogModal);
    });
}

export function bindDialogLikeDrag(el: HTMLElement, handleSelector: string) {
    let dxTotal = 0;
    let dyTotal = 0;
    let startMs = 0;
    let baseBackdropAlpha = 0.4;
    let releaseTimer: number | null = null;
    let dragging = false;
    let currentX = 0;
    let currentY = 0;
    let lastDrawX: number | null = null;
    let lastDrawY: number | null = null;
    let lastOverlayAlpha: number | null = null;
    let dragActivateOffsetX = 0;
    let dragActivateOffsetY = 0;
    let dragStartX = 0;
    let dragStartY = 0;

    const getModalEl = () => el.closest<HTMLElement>('dialog.modal, div.modal');
    const config = readModalDragConfig(getModalEl());
    const getBaseBackdropAlpha = (): number => {
        const modalEl = getModalEl();
        if (!modalEl) return 0.4;
        return readCssNumber(modalEl, '--overlay-alpha-open', 0.4);
    };

    const resetStyles = (durationMs: number): void => {
        const modalEl = getModalEl();
        el.style.transition = `transform ${durationMs}ms ease, opacity ${durationMs}ms ease`;
        if (modalEl) modalEl.style.transition = `background-color ${durationMs}ms ease`;
    };

    const clearStyles = (): void => {
        const modalEl = getModalEl();
        el.style.removeProperty('transition');
        el.style.removeProperty('transform');
        el.style.removeProperty('opacity');
        currentX = 0;
        currentY = 0;
        lastDrawX = null;
        lastDrawY = null;
        lastOverlayAlpha = null;
        if (modalEl) {
            modalEl.style.removeProperty('transition');
            modalEl.style.removeProperty('--overlay-alpha');
        }
        if (releaseTimer !== null) {
            window.clearTimeout(releaseTimer);
            releaseTimer = null;
        }
    };

    const onStart = (): void => {
        if (releaseTimer !== null) {
            window.clearTimeout(releaseTimer);
            releaseTimer = null;
        }
        dxTotal = 0;
        dyTotal = 0;
        dragging = true;
        currentX = 0;
        currentY = 0;
        lastDrawX = null;
        lastDrawY = null;
        lastOverlayAlpha = null;
        dragActivateOffsetX = 0;
        dragActivateOffsetY = 0;
        dragStartX = 0;
        dragStartY = 0;
        startMs = performance.now();
        const modalEl = getModalEl();
        baseBackdropAlpha = getBaseBackdropAlpha();
        el.style.transition = 'none';
        if (modalEl) {
            modalEl.style.transition = 'none';
            modalEl.style.setProperty('--overlay-alpha', String(baseBackdropAlpha));
        }
    };

    const onMove = (event: Interact.DragEvent): void => {
        if (!dragging) return;
        dxTotal += event.dx;
        dyTotal += event.dy;
        if (dragActivateOffsetX === 0 && dragActivateOffsetY === 0) {
            dragActivateOffsetX = dxTotal;
            dragActivateOffsetY = dyTotal;
        }

        const effectiveX = dxTotal - dragActivateOffsetX;
        const effectiveY = dyTotal - dragActivateOffsetY;
        if (dragStartX === 0 && dragStartY === 0) {
            dragStartX = effectiveX;
            dragStartY = effectiveY;
        }
        const distance = Math.max(Math.abs(effectiveX), Math.abs(effectiveY));
        const modalEl = getModalEl();
        const overlayAlpha = baseBackdropAlpha * Math.max(0, 1 - distance / 360);
        currentX = effectiveX;
        currentY = effectiveY;

        if (
            lastDrawX === null ||
            lastDrawY === null ||
            Math.abs(lastDrawX - effectiveX) > 0.01 ||
            Math.abs(lastDrawY - effectiveY) > 0.01
        ) {
            el.style.transform = `translate3d(${effectiveX}px, ${effectiveY}px, 0)`;
            lastDrawX = effectiveX;
            lastDrawY = effectiveY;
        }
        if (modalEl && (lastOverlayAlpha === null || Math.abs(lastOverlayAlpha - overlayAlpha) > 0.002)) {
            modalEl.style.setProperty('--overlay-alpha', String(overlayAlpha));
            lastOverlayAlpha = overlayAlpha;
        }
    };

    const onEnd = (): void => {
        if (!dragging) return;
        dragging = false;
        const elapsed = Math.max(1, performance.now() - startMs);
        const x = currentX;
        const y = currentY;
        const distance = Math.max(Math.abs(x - dragStartX), Math.abs(y - dragStartY));
        const speed = distance / elapsed;
        const shouldClose = shouldCloseByDistanceOrFlick(
            distance,
            config.closeDistancePx,
            speed,
            config.closeSpeedThreshold,
            config.flickMinDistancePx
        );
        const adaptiveDurationMs = getReleaseDurationMs(speed);
        const returnDurationMs = DEFAULT_RELEASE_PROFILE.normal;
        const closeDurationMs = config.closeDurationMs ?? adaptiveDurationMs;

        if (!shouldClose) {
            resetStyles(returnDurationMs);
            const modalEl = getModalEl();
            el.style.transform = 'translate3d(0, 0, 0)';
            if (modalEl) modalEl.style.setProperty('--overlay-alpha', String(baseBackdropAlpha));
            releaseTimer = window.setTimeout(() => clearStyles(), returnDurationMs + 20);
            return;
        }

        resetStyles(closeDurationMs);
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const outX = Math.abs(x) >= Math.abs(y) ? (x >= 0 ? viewportW : -viewportW) : 0;
        const outY = Math.abs(y) > Math.abs(x) ? (y >= 0 ? viewportH : -viewportH) : 0;
        const modalEl = getModalEl();

        el.style.transform = `translate3d(${outX}px, ${outY}px, 0)`;
        el.style.opacity = '0';
        if (modalEl) modalEl.style.setProperty('--overlay-alpha', '0');

        if (modalEl) {
            releaseTimer = window.setTimeout(() => {
                closeModal(modalEl);
                clearStyles();
            }, closeDurationMs + 20);
        } else {
            clearStyles();
        }
    };

    return interact(el).draggable({
        allowFrom: handleSelector || undefined,
        startAxis: 'xy',
        lockAxis: 'start',
        listeners: {
            start() {
                onStart();
            },
            move(event) {
                onMove(event);
            },
            end() {
                onEnd();
            },
        },
    });
}

function readModalDragConfig(modalEl: HTMLElement | null) {
    const dataset = modalEl?.dataset;
    return {
        closeDistancePx: readNumberSetting(dataset?.modalCloseDistancePx, DRAG_CLOSE_DISTANCE_PX, 0),
        closeSpeedThreshold: readNumberSetting(
            dataset?.modalCloseSpeedThreshold,
            DEFAULT_DRAG_BASE.closeSpeedThreshold,
            0
        ),
        flickMinDistancePx: readNumberSetting(
            dataset?.modalFlickMinDistancePx,
            DEFAULT_DRAG_BASE.flickMinDistancePx,
            0
        ),
        returnDurationMs: readOptionalNumberSetting(dataset?.modalReturnDurationMs, 0),
        closeDurationMs: readOptionalNumberSetting(dataset?.modalCloseDurationMs, 0),
    };
}
